/**
 * @fileoverview BMaps 3D visualization with PyMOL compiled to WebAssembly (pymol-web).
 *
 * The visualizer seam is synchronous; the wasm payload needs seconds to start. So this class
 * is a SHADOW MODEL: every interface call changes plain JavaScript state and dirty flags and
 * returns. `AddAtoms` maps atoms whether or not PyMOL exists yet, `VisAtom` / `AppAtom` /
 * `atomIsVisible` answer from the shadow model, and `RedisplayViewer()` is the one place
 * where PyMOL is told anything (synchronously once it is ready; before that the request is
 * remembered and the ready-promise flushes).
 *
 * Milestone M1 = protein + ligand: atoms, styles, colors, selection, camera, picking.
 * Milestone M2 = surfaces (PyMOLSurface.js), shapes with pick-back (PyMOLShapes.js), labels
 * (PyMOLLabels.js) and temporary molecules (PyMOLTempMolecules.js), each behind the same
 * shadow-model rule: synchronous handles and ids, PyMOL work in the flush. See NOTES.md.
 */

import { VisualizerInterface, VisualizerLoadFailedEvent } from '../VisualizerInterface';
import { EventBroker } from '../eventbroker';
import {
    GetChainColor, SelectionColor, ColorStyles, MaestroLikeColors, secondaryStructureColor,
} from '../themes';
import { MolStyles, RepNames } from '../style_manager';
import { AtomGroupTypes } from '../model/atomgroups';
import { isNeutralCamera, neutralFromPyMOLView, pymolViewFromNeutral } from '../visualizerCamera';
import {
    ObjectKinds, PROTEIN_OBJECT_PER_CHAIN, ScratchSelection2, InitialStyle, DefaultCartoonType,
    ObjectPrefix, DefaultSurfaceOpacity, getStyleSpec, elementColor, colorToInt, RayMaxDimension,
} from './PyMOLConstants';
import { PyMOLScene } from './PyMOLScene';
import { PyMOLCommands } from './PyMOLCommands';
import {
    ProbeView, rotatePyMOLView, zoomPyMOLView, panPyMOLView, resetPyMOLViewOrientation,
} from './PyMOLCameraMotion';
import {
    describeAtomObjects, exportObjectNames, asObjectName, unusedObjectName,
} from './PyMOLObjectNames';
import { PyMOLAtomRenderer } from './PyMOLAtomRenderer';
import { PyMOLPicking } from './PyMOLPicking';
import { PyMOLShapes, normalizedOpacity } from './PyMOLShapes';
import { PyMOLSurfaces } from './PyMOLSurface';
import { PyMOLTempMolecules } from './PyMOLTempMolecules';
import { PyMOLLabels, labelExportSettings } from './PyMOLLabels';
import { viewerProjection } from './PyMOLProjection';
import { setPngDPI } from './PyMOLPngDPI';

const WaterGroupTypes = [AtomGroupTypes.CrystalWater, AtomGroupTypes.ComputedWater];
const CompoundGroupTypes = [
    AtomGroupTypes.Compound, AtomGroupTypes.Ligand, AtomGroupTypes.PeptideLigand,
];
const PolymerGroupTypes = [
    AtomGroupTypes.Residue, AtomGroupTypes.Polymer, AtomGroupTypes.Protein,
];
const WaterResidueNames = ['HOH', 'WAT', 'H2O', 'DOD'];
/** Distance (Angstrom) kept around the atoms that ZoomToAtoms() fits into view. */
const ZoomBuffer = 2;

const isPosition = (p) => !!p
    && Number.isFinite(p[0]) && Number.isFinite(p[1]) && Number.isFinite(p[2]);

/** @type {boolean|undefined} cached answer of VisualizerPyMOL.isSupported() */
let webgl2Supported;

function probeWebGL2() {
    if (typeof document === 'undefined') return true;
    try {
        const gl = document.createElement('canvas').getContext('webgl2');
        if (!gl) return false;
        const lose = gl.getExtension('WEBGL_lose_context');
        if (lose) lose.loseContext(); // browsers cap live contexts: free the probe now
        return true;
    } catch (ex) {
        return false;
    }
}

/** The value that occurs most often (the first of them on a tie). */
function mostCommon(values) {
    const counts = new Map();
    for (const value of values) counts.set(value, (counts.get(value) || 0) + 1);
    let best;
    for (const [value, count] of counts) {
        if (best === undefined || count > counts.get(best)) best = value;
    }
    return best;
}

export class VisualizerPyMOL extends VisualizerInterface {
    static get backendName() { return 'pymol'; }

    /**
     * The payload renders through WebGL 2. Probed once with a throw-away canvas, whose context
     * is released at once. Outside a browser (Node, SSR) the answer is "yes": nothing renders.
     */
    static isSupported() {
        if (webgl2Supported === undefined) webgl2Supported = probeWebGL2();
        return webgl2Supported;
    }

    static get requirement() { return 'WebGL 2'; }

    constructor(options) {
        super(options); // in a browser this runs InitViewer(options)
        if (!this.atomsById) this.initState(); // Node / SSR: InitViewer did not run
        this.Reset = this.Reset.bind(this);
    }

    // ------------------------------------------------------------------ setup

    initState() {
        /** @type {Map<number, object>} backend id -> shadow atom */
        this.atomsById = new Map();
        /** @type {Map<object, object>} BMaps atom -> shadow atom */
        this.atomsByAppAtom = new Map();
        /** @type {Map<*, number>} BMaps uniqueID -> backend id */
        this.idsByUniqueID = new Map();
        /** @type {Map<string, Set<object>>} residue key -> shadow atoms (polymer atoms only) */
        this.residues = new Map();
        /** Protein chain ids in order of arrival, for themes.GetChainColor(). */
        this.proteinChains = [];
        this.colorStyle = ColorStyles.bmaps; // the ColorStyle preference (SetColorStyle)
        /** Ids grow monotonically and are never reused, across ALL PyMOL objects. */
        this.nextAtomId = this.nextAtomId || 1;
        /** Identity numbers of atom groups / cases, for object keys. */
        this.identitySeq = new WeakMap();
        this.nextIdentity = 1;

        this.renderer = new PyMOLAtomRenderer({
            resolveColor: (visAtom) => this.effectiveColor(visAtom),
            // the cartoon of a rep set that mixes cartoon with atom reps (spec.cartoonColored)
            resolveCartoonColor: (visAtom) => this.residueColor(visAtom),
            lookupVisAtom: (appAtom) => this.atomsByAppAtom.get(appAtom),
            // given up after repeated load failures: not drawn, so not mapped either
            onObjectGivenUp: (obj) => {
                for (const visAtom of obj.atoms.values()) this.UnmapAtom(visAtom.appAtom);
            },
        });
        /** RedisplayViewer() was called before the viewer was ready. */
        this.flushPending = false;
        this.flushing = false;
        this.flushAgain = false;
        /** `delete all` is owed to PyMOL (ZapAll). */
        this.zapPending = false;
        /** Camera requests in call order, run after the structure is in PyMOL. */
        this.cameraOps = [];

        this.initM2State();
    }

    /** Milestone M2 registries: surfaces, shapes, labels, temporary molecules. */
    initM2State() {
        this.nextHandleId = this.nextHandleId || 1;
        this.surfaces = new PyMOLSurfaces({ allocateId: () => this.nextAtomId++ });
        this.shapes = new PyMOLShapes();
        this.tempMolecules = new PyMOLTempMolecules({
            allocateIds: (n) => Int32Array.from({ length: n }, () => this.nextAtomId++),
        });
        if (this.labels) this.labels.clear();
        else this.labels = new PyMOLLabels();
        if (this.repOrder) {
            this.surfaces.setRepOrder(this.repOrder);
            this.tempMolecules.setRepOrder(this.repOrder);
        }
    }

    /**
     * @param {object} [options] `parentEltId`, `backgroundColor` as for Visualizer3Dmol
     *   (`cartoonQuality` has no PyMOL equivalent in M1). For tests and harnesses:
     *   `viewerLoader` (replaces the payload import), `parentElement`, `pymolPayloadURL`,
     *   `hoverDwellMs`.
     */
    InitViewer(options={}) {
        if (this.scene) this.dispose();
        this.initState();
        this.picking = new PyMOLPicking({
            publish: (target, event) => this.publishMouseEvent(target, event),
            targetForPick: (pick) => this.targetForPick(pick),
            atomForId: (id) => this.atomsById.get(id) || null,
            hitTestExtras: (x, y) => this.hitTestExtras(x, y),
            hoverDwellMs: options.hoverDwellMs,
        });
        this.scene = new PyMOLScene({
            parentEltId: options.parentEltId,
            parentElement: options.parentElement,
            viewerLoader: options.viewerLoader,
            payloadURL: options.pymolPayloadURL,
        });
        // The canvas has to exist when this returns: paste_drag.js looks for it right away.
        const canvas = this.scene.createCanvas();
        this.picking.attachCanvas(canvas);
        this.labels.attachCanvas(canvas);
        if (options.backgroundColor) this.SetBackgroundColor(options.backgroundColor);
        /** Resolves once the viewer is ready and everything buffered so far is flushed. */
        this.whenReady = this.scene.start().then((viewer) => {
            const reps = viewer.constructor && viewer.constructor.REPS;
            this.renderer.setRepOrder(reps);
            if (Array.isArray(reps)) {
                this.repOrder = reps;
                this.surfaces.setRepOrder(reps);
                this.tempMolecules.setRepOrder(reps);
            }
            this.picking.attachViewer(viewer);
            // The label overlay follows every frame PyMOL draws with another view (mouse).
            this.offFrame = viewer.on('frame', () => this.updateLabels());
            // numpy: the wrapper (payload >= 0.1.2) installs and imports it in idle time after
            // start-up; until then the bridge loads ghost-pose coordinates from plain lists. Do not
            // import it here - that could run while Pyodide is still loading numpy's libraries.
            if (this.flushPending) this.flush();
            // A camera token or a camera motion does not depend on the structure; a zoom waits
            // for a flush, and anything queued after it waits with it (call order).
            else if (this.cameraOps.every((op) => !op.zoom)) this.runCameraOps();
            return viewer;
        });
        // A load failure is already reported by the scene (console + status line). A throw in
        // the handler above - after the viewer loaded - is not: report it the same way, so the
        // app falls back to 3Dmol either way instead of keeping a viewer that never draws.
        const { scene } = this;
        this.whenReady.catch((err) => {
            if (scene.disposed) return;
            if (!scene.failed) {
                scene.failed = err;
                console.error('PyMOL backend: setting up the loaded viewer failed', err);
                scene.setStatus('The 3D viewer failed to start (see the browser console).');
            }
            EventBroker.publish(VisualizerLoadFailedEvent, {
                backend: VisualizerPyMOL.backendName,
                error: String((err && err.message) || err),
            });
        });
    }

    /** Seam: see VisualizerInterface.Dispose (e.g. after the payload failed to load). */
    Dispose() { this.dispose(); }

    dispose() {
        if (typeof this.offFrame === 'function') this.offFrame();
        this.offFrame = null;
        if (this.labels) this.labels.dispose();
        if (this.picking) this.picking.detach();
        if (this.scene) this.scene.dispose();
        this.scene = null;
        this.picking = null;
    }

    get viewer() { return this.scene ? this.scene.viewer : null; }

    // ------------------------------------------------------------------ mapping

    /**
     * The mapping is kept in Maps owned by this instance. `appAtom.forVisualizer` is NOT used:
     * it is a single slot that ui/MolVisualizer.jsx (its own Visualizer3Dmol) also writes for
     * compound atoms, which would corrupt either side.
     */
    MapAtoms(appAtom, visAtom) {
        this.atomsByAppAtom.set(appAtom, visAtom);
        this.atomsById.set(visAtom.id, visAtom);
        if (appAtom.uniqueID != null) this.idsByUniqueID.set(appAtom.uniqueID, visAtom.id);
    }

    UnmapAtom(appAtom) {
        const visAtom = this.atomsByAppAtom.get(appAtom);
        if (!visAtom) return;
        this.atomsByAppAtom.delete(appAtom);
        this.atomsById.delete(visAtom.id);
        if (appAtom.uniqueID != null && this.idsByUniqueID.get(appAtom.uniqueID) === visAtom.id) {
            this.idsByUniqueID.delete(appAtom.uniqueID);
        }
    }

    UnmapAtoms(appAtoms) {
        for (const appAtom of appAtoms) this.UnmapAtom(appAtom);
    }

    VisAtom(appAtom) {
        return (appAtom && this.atomsByAppAtom.get(appAtom)) || null;
    }

    AppAtom(visAtom) {
        return visAtom ? visAtom.appAtom : null;
    }

    GetAtomByUniqueID(uniqueID) {
        const id = this.idsByUniqueID.get(uniqueID);
        return id == null ? null : (this.atomsById.get(id) || null);
    }

    /** A pick names (object, ID); ids are unique across our objects, user objects are not ours. */
    atomForPick(pick) {
        const visAtom = this.atomsById.get(pick.id);
        if (!visAtom || this.renderer.objectNameFor(visAtom) !== pick.object) return null;
        return visAtom;
    }

    /**
     * What a click on `pick` means for BMaps (3Dmol: the first CLICKABLE object on the ray).
     *   our atom -> {atom}; a shape with onClick -> {shape: the very handle};
     *   a shape without onClick, a temporary molecule (3Dmol: not clickable) -> {passThrough},
     *   which PyMOLPicking resolves to the atom under the pointer;
     *   nothing, or an object BMaps does not manage -> background - unless a clickable shape
     *   that PyMOL cannot pick (a transparent one) is under the pointer.
     */
    targetForPick(pick) {
        if (pick.type === 'object:molecule') {
            const visAtom = this.atomForPick(pick);
            if (visAtom) return { atom: visAtom };
            if (this.tempMolecules.isPoolName(pick.object)) {
                return this.transparentClickableAt(pick) || { passThrough: true };
            }
            return { background: true };
        }
        if (pick.type === 'object:cgo') {
            const handle = this.shapes.handleForPick(pick.object, pick.index);
            if (handle) return handle.onClick ? { shape: handle } : { passThrough: true };
            return { background: true };
        }
        return this.transparentClickableAt(pick) || { background: true };
    }

    /** A clickable transparent shape at the pick position (PyMOL does not pick transparent CGO). */
    transparentClickableAt(pick) {
        if (this.shapes.size === 0) return null;
        const hit = this.shapes.hitTest(viewerProjection(this.viewer), pick.cssX, pick.cssY,
            (handle) => !!handle.onClick && normalizedOpacity(handle.opacity) < 0.995);
        return hit ? { shape: hit.handle } : null;
    }

    /**
     * Hover targets PyMOL does not know about, front-most first: shapes with a description
     * (3Dmol prepShape: hoverable) and atoms of temporary molecules (hoverable, not clickable).
     * @returns {{key: object, target: object, front: number}|null}
     */
    hitTestExtras(x, y) {
        if (this.shapes.size === 0 && this.tempMolecules.size === 0) return null;
        const projection = viewerProjection(this.viewer);
        if (!projection) return null;
        const shape = this.shapes.hitTest(projection, x, y, (handle) => !!handle.description);
        const temp = this.tempMolecules.hitTest(projection, x, y);
        if (shape && (!temp || shape.front <= temp.front)) {
            return { key: shape.handle, target: { shape: shape.handle }, front: shape.front };
        }
        if (temp) return { key: temp.target, target: { atom: temp.target }, front: temp.front };
        return null;
    }

    identityOf(object) {
        if (!object || (typeof object !== 'object' && typeof object !== 'function')) return 0;
        let seq = this.identitySeq.get(object);
        if (!seq) {
            seq = this.nextIdentity++;
            this.identitySeq.set(object, seq);
        }
        return seq;
    }

    /**
     * Which PyMOL object an atom belongs to. See NOTES.md "Object partitioning".
     * @returns {{kind: string, key: string}}
     */
    partitionFor(atom) {
        const group = typeof atom.getAtomGroup === 'function' ? atom.getAtomGroup() : null;
        const type = group ? group.type : null;
        const caseData = typeof atom.getCaseData === 'function' ? atom.getCaseData() : null;
        const caseId = this.identityOf(caseData);
        // One model number per object: PyMOL turns model numbers into states.
        const model = Number.isInteger(atom.modelNum) && atom.modelNum > 0 ? atom.modelNum : 0;
        const fragment = atom.fragment || null;

        if (type === 'Hotspot' || (fragment && fragment.isHotspot)) {
            return { kind: ObjectKinds.hotspot, key: `hot:${caseId}` };
        }
        if (WaterGroupTypes.includes(type) || (fragment && fragment.isWater)
            || WaterResidueNames.includes(atom.resn)) {
            return { kind: ObjectKinds.water, key: `wat:${caseId}:${model}` };
        }
        if (fragment || type === AtomGroupTypes.Fragment) {
            return { kind: ObjectKinds.fragment, key: `frag:${caseId}` };
        }
        if (CompoundGroupTypes.includes(type)) {
            return { kind: ObjectKinds.compound, key: `cmp:${this.identityOf(group)}` };
        }
        if (!atom.hetflag || PolymerGroupTypes.includes(type)) {
            const chain = PROTEIN_OBJECT_PER_CHAIN ? (atom.chain || '') : '*';
            return { kind: ObjectKinds.protein, key: `pro:${caseId}:${model}:${chain}` };
        }
        if (group) return { kind: ObjectKinds.hetero, key: `het:${caseId}:${model}` };
        return { kind: ObjectKinds.misc, key: `misc:${caseId}:${model}` };
    }

    /** Build (but do not map) the shadow record of a BMaps atom. */
    MakeAtom(atom) {
        const { kind, key } = this.partitionFor(atom);
        const isPolymerAtom = kind === ObjectKinds.protein;
        return {
            id: this.nextAtomId++,
            appAtom: atom,
            objectKind: kind,
            objectKey: key,
            // the chain is spelled out: with PROTEIN_OBJECT_PER_CHAIN off the object key has none
            residueKey: isPolymerAtom ? `${key}|${atom.chain || ''}|${atom.resi}|${atom.icode || ''}` : null,
            style: getStyleSpec(atom.initialStyle) ? atom.initialStyle : InitialStyle,
            colorOverride: null,
            selected: false,
            // what PyMOL was last told (maintained by the renderer)
            pushedStyle: null,
            pushedColor: null,
            pushedSettings: {},
            pushedCartoonType: DefaultCartoonType,
        };
    }

    // MakeBond(): not needed. Bonds are read from the atoms when an object is written as CIF.

    /**
     * Load BMaps atoms. The mapping is complete when this returns, with or without PyMOL.
     * Bonds come from the atoms themselves (atom.getBonds()), like in Visualizer3Dmol.
     * @returns {object[]} the new shadow atoms
     */
    AddAtoms(atoms) {
        const added = [];
        for (const atom of [].concat(atoms || [])) {
            if (!atom || this.atomsByAppAtom.has(atom)) continue;
            const visAtom = this.MakeAtom(atom);
            this.MapAtoms(atom, visAtom);
            this.renderer.addAtom(visAtom);
            if (visAtom.residueKey) {
                let residue = this.residues.get(visAtom.residueKey);
                if (!residue) {
                    residue = new Set();
                    this.residues.set(visAtom.residueKey, residue);
                }
                residue.add(visAtom);
                if (atom.chain && !this.proteinChains.includes(atom.chain)) {
                    this.proteinChains.push(atom.chain);
                }
            }
            added.push(visAtom);
        }
        return added;
    }

    RemoveAtoms(atomsIn) {
        let removed = 0;
        for (const atom of [].concat(atomsIn || [])) {
            const visAtom = this.atomsByAppAtom.get(atom);
            if (!visAtom) continue;
            this.renderer.removeAtom(visAtom);
            this.UnmapAtom(atom);
            if (this.picking) this.picking.forgetAtom(visAtom);
            const residue = visAtom.residueKey && this.residues.get(visAtom.residueKey);
            if (residue) {
                residue.delete(visAtom);
                if (residue.size === 0) this.residues.delete(visAtom.residueKey);
                else this.markResidueColorDirty(visAtom);
            }
            removed++;
        }
        if (removed > 0) console.log(`Removed and unmapped ${removed} atoms`);
    }

    /**
     * Remove the BMaps ATOMS (the scene is rebuilt by displayAll()); objects the user made in
     * PyMOL are kept. Surfaces, shapes, labels and temporary molecules stay until the app
     * removes them, exactly as with Visualizer3Dmol (its Reset() removes only the model): the
     * app's trackers rely on that - e.g. ligand_halos and display_mgr's active-compound ghost
     * skip re-creating what they believe is still drawn. See NOTES.md "Reset and ZapAll".
     */
    Reset() {
        this.renderer.resetAll();
        this.atomsById.clear();
        this.atomsByAppAtom.clear();
        this.idsByUniqueID.clear();
        this.residues.clear();
        this.proteinChains = [];
        // hover state of the atoms only; shapes and temporary molecules stay
        if (this.picking) this.picking.forgetTargetsWhere((key) => !!key && key.objectKey != null);
        this.Redisplay();
    }

    /**
     * Remove everything: BMaps atoms, surfaces, shapes, labels, temporary molecules, and what
     * the user created through the native command line (`delete all` at the next flush).
     */
    ZapAll() {
        this.zapPending = true;
        this.initM2State();
        if (this.picking) this.picking.forgetTarget(null);
        this.Reset();
    }

    /**
     * Live switching: remove everything now (the zap is flushed at once, so the hidden PyMOL
     * instance holds nothing), stop the frame loop and hide the canvas, labels and status line.
     * The instance stays: its Pyodide runtime cannot be unloaded, and switching back is instant.
     */
    Deactivate() {
        this.active = false;
        this.ZapAll();
        this.RedisplayViewer();
        this.setOverlaysVisible(false);
        if (this.scene) this.scene.setActive(false);
    }

    Activate() {
        this.active = true;
        this.setOverlaysVisible(true);
        if (this.scene) this.scene.setActive(true);
    }

    setOverlaysVisible(visible) {
        const layer = this.labels && this.labels.layer;
        if (layer && layer.style) layer.style.display = visible ? '' : 'none';
    }

    // ------------------------------------------------------------------ display

    ResizeViewer() {
        if (this.scene) this.scene.resize();
    }

    SetBackgroundColor(colorInfo) {
        if (this.scene) this.scene.setBackgroundColor(colorInfo);
    }

    /** THE flush point. Base-class Redisplay() calls this only while redisplay is not paused. */
    RedisplayViewer() {
        if (!this.viewer) {
            this.flushPending = true;
            return;
        }
        this.flush();
    }

    flush() {
        const { viewer } = this;
        if (!viewer) return;
        if (this.flushing) { // a mouse handler or listener asked again from inside a flush
            this.flushAgain = true;
            return;
        }
        this.flushing = true;
        this.flushPending = false;
        try {
            do {
                this.flushAgain = false;
                if (this.zapPending) {
                    this.zapPending = false;
                    viewer.cmd.delete('all');
                    this.renderer.forgetDeadObjects();
                }
                this.renderer.flush(viewer);
                // after the atoms: nothing below depends on them being loaded, but a zoom does
                this.surfaces.flush(viewer);
                this.tempMolecules.flush(viewer);
                this.shapes.flush(viewer);
                this.drawNewestSurfaceFirst(viewer);
                this.drawInvisibleAtomsLast(viewer);
                this.runCameraOps();
                this.refreshHoverAtoms();
                this.updateLabels(true);
                // No viewer.requestRedraw(): every command above raises PyMOL's own redisplay
                // flag, and the wrapper's rAF loop draws ONCE per frame. requestRedraw() is
                // cmd.refresh(), which on this (GUI) thread is refresh_now = a synchronous full
                // frame inside the app's call stack (VERIFIED: 120-270 ms per Redisplay() with
                // a 300-pose cloud; a change followed by requestRedraw leaves nothing to draw
                // for the next tick).
            } while (this.flushAgain);
        } catch (ex) {
            console.error('PyMOL backend: redisplay failed', ex);
        } finally {
            this.flushing = false;
        }
    }

    /**
     * Two surfaces over the same atoms (the binding-site surface and the protein 'surface'
     * style) have the same geometry. 3Dmol (three.js, depth test LEQUAL) shows the one drawn
     * LAST; PyMOL's depth test (LESS) keeps the one drawn FIRST - the new protein surface
     * stayed invisible under the binding-site surface (VERIFIED in the real-app scenario). So
     * after a surface is created the older ones are re-enabled, which draws them after it.
     */
    drawNewestSurfaceFirst(viewer) {
        const created = this.surfaces.lastStats.createdNames || [];
        if (created.length === 0) return;
        for (const name of this.surfaces.loadedNames()) {
            if (created.includes(name)) continue;
            viewer.cmd.disable(name);
            viewer.cmd.enable(name);
        }
    }

    /**
     * `invisible` atoms are fully transparent spheres, and PyMOL's transparent pass writes
     * depth: whatever transparent thing is drawn AFTER them (a hotspot's own surface, halos,
     * ghosts) disappears behind them (VERIFIED: a transparent surface around invisible spheres
     * lit 0.5 % of the view instead of 33 %). PyMOL draws objects in the order they were
     * enabled, so objects holding invisible atoms are re-enabled - moved to the end - whenever
     * an object was created or the set of such objects changed. Picking is unaffected.
     */
    drawInvisibleAtomsLast(viewer) {
        const names = this.renderer.loadedObjectsWithStyle(MolStyles.invisible);
        const key = names.join(' ');
        const created = this.renderer.lastStats.loaded + this.surfaces.lastStats.loaded
            + this.shapes.lastStats.built + this.tempMolecules.lastStats.loaded;
        if (names.length > 0 && (created > 0 || key !== this.invisibleOrderKey)) {
            for (const name of names) {
                viewer.cmd.disable(name);
                viewer.cmd.enable(name);
            }
        }
        this.invisibleOrderKey = key;
    }

    /** Hover = the wrapper's JS hit-test over our visible atoms; refetch them when they change. */
    refreshHoverAtoms() {
        if (!this.renderer.hoverStale) return;
        this.renderer.hoverStale = false;
        const names = this.renderer.loadedObjectNames();
        this.viewer.setHoverAtoms(names.length > 0 ? `visible and (${names.join(' or ')})` : null);
    }

    // ------------------------------------------------------------------ camera

    /**
     * `PyMOL.view`: this backend's own token (exact round trip). `neutral`: the backend-neutral
     * camera (visualizerCamera.js), used by another backend or after a live switch.
     */
    GetCameraPosition() {
        const live = this.scene ? this.scene.getView() : null;
        // A camera set but not yet applied - before the viewer is ready, or a neutral camera
        // waiting for the structure it is relative to (right after a live switch, when the app
        // may read the camera back at once) - IS the camera, as far as the app can tell.
        const pending = [...this.cameraOps].reverse().find((op) => op.view || op.neutral);
        let view = live;
        if (pending && pending.view) {
            view = [...pending.view];
        } else if (pending && pending.neutral) {
            if (!live) return { neutral: pending.neutral };
            view = pymolViewFromNeutral(pending.neutral, live);
        }
        if (!view) return null;
        return { PyMOL: { view }, neutral: neutralFromPyMOLView(view) };
    }

    /**
     * Our own token wins; otherwise a backend-neutral camera (e.g. from 3Dmol before a live
     * switch, or a session saved there). The neutral camera runs AFTER any zoom still queued:
     * it keeps the depth span of the slab that zoom chose (pymolViewFromNeutral).
     */
    SetCameraPosition(camera) {
        const view = camera && camera.PyMOL && camera.PyMOL.view;
        if (Array.isArray(view) && view.length === 18 && view.every(Number.isFinite)) {
            this.cameraOps = [{ view: [...view] }]; // the latest explicit camera wins
            if (this.viewer) this.runCameraOps();
            return;
        }
        if (camera && isNeutralCamera(camera.neutral)) {
            this.cameraOps.push({ neutral: camera.neutral });
            if (this.viewer && !this.renderer.hasPendingStructure()) this.runCameraOps();
        }
    }

    /**
     * @param {(atom: object) => boolean} [selectorFn] BMaps-atom predicate; none = everything.
     * The atoms are chosen now; the camera moves once they are in PyMOL (the next Redisplay,
     * or immediately when nothing is waiting to be loaded).
     */
    ZoomToAtoms(selectorFn) {
        /** @type {Map<string, number[]|null>} object key -> ids, null = the whole object */
        const targets = new Map();
        for (const obj of this.renderer.objects.values()) {
            if (!selectorFn) {
                targets.set(obj.key, null);
                continue;
            }
            const ids = [];
            for (const visAtom of obj.atoms.values()) {
                if (selectorFn(visAtom.appAtom)) ids.push(visAtom.id);
            }
            if (ids.length === obj.atoms.size) targets.set(obj.key, null);
            else if (ids.length > 0) targets.set(obj.key, ids);
        }
        this.cameraOps.push({ zoom: targets });
        if (this.viewer && !this.renderer.hasPendingStructure()) this.runCameraOps();
    }

    /**
     * Camera motion (RotateView ... ResetCameraOrientation). Same contracts as Visualizer3Dmol,
     * computed by PyMOLCameraMotion.js through the same view transforms. The motion joins the
     * camera queue so it runs in call order after any zoom or camera still waiting for the
     * structure (e.g. ResetView's orientation reset before its redraw's zoom). Its arguments are
     * checked NOW, so a bad one throws at the call as it does on 3Dmol, not later in the queue.
     * GetCameraPosition() does not anticipate a motion that is still queued.
     * @param {(view: number[]) => number[]} motion
     */
    queueCameraMotion(motion) {
        motion(ProbeView);
        this.cameraOps.push({ motion });
        if (this.viewer && !this.renderer.hasPendingStructure()) this.runCameraOps();
    }

    RotateView(degrees, axis, pivot) {
        this.queueCameraMotion((view) => rotatePyMOLView(view, degrees, axis, pivot));
    }

    ZoomView(factor) {
        this.queueCameraMotion((view) => zoomPyMOLView(view, factor));
    }

    PanView(delta) {
        this.queueCameraMotion((view) => panPyMOLView(view, delta));
    }

    ResetCameraOrientation() {
        this.queueCameraMotion(resetPyMOLViewOrientation);
    }

    runCameraOps() {
        const { viewer } = this;
        const ops = this.cameraOps;
        this.cameraOps = [];
        for (const op of ops) {
            try {
                if (op.view) viewer.setView(op.view);
                else if (op.neutral) {
                    viewer.setView(pymolViewFromNeutral(op.neutral, viewer.getView()));
                } else if (op.motion) {
                    viewer.setView(op.motion(viewer.getView()));
                } else this.zoomNow(op.zoom);
            } catch (ex) {
                console.error('PyMOL backend: camera update failed', ex);
            }
        }
        // setView / zoom raise PyMOL's redisplay flag themselves (see flush())
    }

    zoomNow(targets) {
        const { viewer } = this;
        const whole = [];
        const partial = [];
        for (const [key, ids] of targets) {
            const obj = this.renderer.getObject(key);
            if (obj && obj.loaded) {
                if (ids) partial.push([obj, ids]);
                else whole.push(obj.name);
            }
        }
        if (whole.length === 0 && partial.length === 0) return;
        let selection = whole.join(' or ');
        if (partial.length > 0) {
            viewer.cmd.select(ScratchSelection2, whole.length > 0 ? selection : 'none');
            for (const [obj, ids] of partial) {
                this.renderer.withSelection(viewer, obj, ids,
                    (sel) => viewer.cmd.select(ScratchSelection2, `${ScratchSelection2} or ${sel}`));
            }
            selection = ScratchSelection2;
        }
        try {
            viewer.cmd.zoom(selection, ZoomBuffer, { animate: 0 });
        } finally {
            if (partial.length > 0) viewer.cmd.delete(ScratchSelection2);
        }
    }

    /** PyMOL's own CPU ray tracer is available for image export. */
    SupportsRayTracedImage() { return true; }

    /**
     * Pixel size of a ray-traced export: `width` pixels across (the canvas aspect ratio gives the
     * height), or `scale` x the canvas drawing buffer when no width is asked for. Capped so the
     * longer side stays <= RayMaxDimension (aspect ratio kept) - the ray tracer holds the whole
     * image in memory.
     * @param {{width?: number, scale?: number}|number} [request] a bare number is a scale
     * @returns {{width: number, height: number, scale: number}|null}
     */
    RayTracedImageSize(request=1) {
        const { width=0, scale=1 } = (typeof request === 'number' ? { scale: request } : request)
            || {};
        const size = this.scene && this.scene.pixelSize();
        if (!size) return null;
        // an explicit width may be smaller than the screen; a scale is a multiple of it
        const wanted = width > 0 ? width / size.width : Math.max(1, Number(scale) || 1);
        const s = Math.min(wanted, RayMaxDimension / Math.max(size.width, size.height));
        return {
            width: Math.max(1, Math.round(size.width * s)),
            height: Math.max(1, Math.round(size.height * s)),
            scale: s,
        };
    }

    /**
     * The canvas as an image. The label overlay is not in the canvas: when labels are shown they
     * are painted onto the captured frame, as 3Dmol's export (sprites in its GL scene) has them.
     * `{ray: true, width|scale, dpi, outline, transparentBackground}` renders it with PyMOL's ray
     * tracer instead of capturing the GL frame (blocks the page while it renders). `dpi` does not
     * change the pixels: it is the resolution the image is tagged with, i.e. how big it is meant
     * to be on paper.
     */
    GetImageBlob(options={}) {
        if (!this.scene) return Promise.resolve(null);
        let capture;
        const dpi = options.ray && Number(options.dpi) > 0 ? Number(options.dpi) : 0;
        if (options.ray) {
            const size = this.RayTracedImageSize(options);
            if (!size) return Promise.resolve(null);
            capture = this.scene.rayTrace({
                width: size.width,
                height: size.height,
                dpi,
                outline: !!options.outline,
                transparentBackground: !!options.transparentBackground,
            });
        } else {
            capture = this.scene.toBlob(options);
        }
        return capture.then((blob) => {
            if (!blob || this.labels.size === 0) return blob;
            this.updateLabels(true);
            const projection = viewerProjection(this.viewer);
            return this.labels.composite(blob, {
                cssWidth: projection ? projection.width : 0,
                type: options.type,
                quality: options.quality,
            }).catch((ex) => {
                console.error('PyMOL backend: labels could not be added to the image', ex);
                return blob;
            // compositing goes through a canvas, which drops PyMOL's resolution tag
            }).then((composited) => (dpi > 0 ? setPngDPI(composited, dpi) : composited));
        });
    }

    /** The `CartoonStyle` preference: PyMOL's or Maestro's ribbon proportions (CartoonProfiles). */
    SetCartoonStyle(name) {
        if (!this.scene) return;
        this.scene.applyCartoonProfile(name);
        if (this.viewer) this.viewer.requestRedraw();
    }

    /**
     * The `ColorStyle` preference (themes.js ColorStyles): decided in effectiveColor /
     * defaultAtomColor for every atom, so every colour is stale; the caller redraws.
     */
    SetColorStyle(name) {
        const style = name === ColorStyles.maestro ? ColorStyles.maestro : ColorStyles.bmaps;
        if (style === this.colorStyle) return;
        this.colorStyle = style;
        if (this.renderer) {
            for (const visAtom of this.atomsById.values()) this.renderer.markColorDirty(visAtom);
        }
    }

    // ------------------------------------------------------------------ session export

    /** PyMOL writes its own session files; BMaps offers one for opening in desktop PyMOL. */
    SupportsNativeSessionExport() { return true; }

    /**
     * The scene as a PyMOL session file (`.pse`), dressed for opening somewhere else:
     *   - BMaps' internal `_bm_*` object names become readable ones - desktop PyMOL hides names
     *     starting with "_" from its object panel, so the file would look empty;
     *   - the labels, an HTML overlay rather than part of the GL scene, become an object of
     *     labelled pseudoatoms;
     *   - PyMOLScene.saveSession restores the embed-only settings to PyMOL's defaults.
     * All of it is undone afterwards: the live scene is exactly as it was.
     *
     * Export only. BMaps does not read a `.pse` back - it is a Python pickle, so loading one is
     * code execution; a BMaps session (UserActions.CaptureState) is what round-trips.
     * @returns {Promise<{blob: Blob, extension: string, format: string}|null>}
     */
    async SaveNativeSession() {
        const { scene, viewer } = this;
        if (!scene || !viewer) return null;
        const objects = this.objectNames();
        // every name PyMOL knows: a rename or pseudoatom onto a selection's name takes it too
        const taken = this.objectNames('all');
        const undo = [];
        try {
            this.dropScratchSelectionsForExport();
            undo.push(...this.renameObjectsForExport(objects, taken));
            undo.push(...this.addLabelObjectForExport(taken));
            const bytes = scene.saveSession({ desktop: true });
            if (!bytes) return null;
            return {
                // application/octet-stream: a .pse is a pickle, and no browser should open it
                blob: new Blob([bytes], { type: 'application/octet-stream' }),
                extension: 'pse',
                format: 'PyMOL session',
            };
        } finally {
            for (const step of undo.reverse()) {
                try {
                    step();
                } catch (ex) {
                    console.error('PyMOL backend: could not undo a session-export change', ex);
                }
            }
        }
    }

    /**
     * The names in the viewer, BMaps' and the user's (empty if the viewer cannot say).
     * @param {'objects'|'selections'|'all'} kind as cmd.get_names
     * @returns {string[]}
     */
    objectNames(kind='objects') {
        try {
            return [...this.viewer.names(kind)];
        } catch (ex) {
            console.warn(`PyMOL backend: could not list the ${kind}`, ex);
            return [];
        }
    }

    /**
     * Delete BMaps' scratch selections (`_bm_sel`, `_bm_sel2`). They exist only around one
     * command and are made again on the next, but one interrupted by an error stays, and would
     * travel in the file. Nothing to put back.
     */
    dropScratchSelectionsForExport() {
        for (const name of this.objectNames('selections')) {
            if (!name.startsWith(ObjectPrefix)) continue;
            try {
                this.viewer.cmd.delete(name);
            } catch (ex) {
                console.warn(`PyMOL backend: could not drop ${name} from the export`, ex);
            }
        }
    }

    /**
     * Give BMaps' objects readable names for the export.
     * @param {string[]} objects existing object names
     * @param {string[]} taken every name in the viewer; the new names are added to it
     * @returns {function[]} steps that put the names back
     */
    renameObjectsForExport(objects, taken) {
        const { viewer } = this;
        const renames = exportObjectNames(objects, describeAtomObjects(this.renderer), taken);
        const undo = [];
        for (const [from, to] of renames) {
            try {
                viewer.cmd.set_name(from, to);
                taken.push(to);
                undo.push(() => viewer.cmd.set_name(to, from));
            } catch (ex) {
                // e.g. a label that is a PyMOL selection keyword: the object keeps its `_bm_`
                // name, hidden in desktop PyMOL's panel but in the file
                console.warn(`PyMOL backend: could not rename ${from} for the export`, ex);
            }
        }
        return undo;
    }

    /**
     * Put the label overlay into the scene as PyMOL labels on pseudoatoms, so the exported file
     * shows what the screen shows (PyMOLLabels says why the live labels are HTML), in BMaps' look:
     * text and background colors per label (labelExportSettings), and for the object the most
     * common size, bold when most are, and on top of the scene like the overlay
     * (`float_labels`). The settings go with the object when it is deleted afterwards.
     * @param {string[]} taken every name in the viewer (the label object takes an unused one)
     * @returns {function[]} steps that remove it again
     */
    addLabelObjectForExport(taken) {
        const { viewer } = this;
        const records = this.labels ? this.labels.records() : [];
        if (records.length === 0) return [];
        const name = unusedObjectName(asObjectName('Labels'), taken);
        try {
            const looks = new Map(); // settings JSON -> atom names
            const sizes = [];
            let bold = 0;
            records.forEach(({ text, anchor, style }, i) => {
                const atomName = `L${i + 1}`;
                // `pos` as PyMOL's own "[x,y,z]" text: the wrapper turns a positional JS array
                // into a Python list, but a keyword argument arrives as a JsProxy, and
                // cmd.pseudoatom only accepts a list, a tuple or that string (creating.py).
                viewer.cmd.pseudoatom(name, {
                    name: atomName, pos: `[${anchor.map(Number).join(',')}]`, label: text,
                });
                const look = labelExportSettings(style);
                const key = JSON.stringify(look.atom);
                if (!looks.has(key)) looks.set(key, []);
                looks.get(key).push(atomName);
                sizes.push(look.size);
                if (look.bold) bold++;
            });
            viewer.cmd.show('labels', name);
            this.styleLabelObjectForExport(name, looks, sizes, bold > records.length / 2);
        } catch (ex) {
            console.warn('PyMOL backend: could not add the labels to the export', ex);
            try { viewer.cmd.delete(name); } catch (ignored) { /* nothing was created */ }
            return [];
        }
        taken.push(name);
        return [() => viewer.cmd.delete(name)];
    }

    /** Settings of the export's label object: per look (atoms by name), then the object's. */
    styleLabelObjectForExport(name, looks, sizes, bold) {
        const { viewer } = this;
        try {
            for (const [key, atomNames] of looks) {
                const sel = `${name} and name ${atomNames.join('+')}`;
                for (const [setting, value] of Object.entries(JSON.parse(key))) {
                    viewer.cmd.set(setting, value, sel);
                }
            }
            viewer.cmd.set('label_size', mostCommon(sizes), name);
            viewer.cmd.set('label_font_id', bold ? 7 : 5, name); // DejaVu Sans (Bold)
            viewer.cmd.set('float_labels', 1, name);
        } catch (ex) {
            // the labels are in the file either way, in PyMOL's look
            console.warn('PyMOL backend: could not give the exported labels BMaps\' look', ex);
        }
    }

    /** Re-project the label overlay (after a flush, and after every frame with another view). */
    updateLabels(force=false) {
        if (!this.labels || this.labels.size === 0 || !this.viewer) return;
        try {
            this.labels.update(viewerProjection(this.viewer), force);
        } catch (ex) {
            console.error('PyMOL backend: label update failed', ex);
        }
    }

    // ------------------------------------------------------------------ native commands
    // Policy, execution and completion live in PyMOLCommands.js (NOTES.md "Command input").

    /** The typed command line, created on first use. */
    get nativeCommands() {
        if (!this.commandLine) this.commandLine = new PyMOLCommands({ visualizer: this });
        return this.commandLine;
    }

    SupportsNativeCommands() { return true; }

    /** Every rep-set representation, incl. per-atom surface / mesh / dots (PyMOL reps). */
    SupportedRepSetReps() { return [...RepNames]; }

    /** Per-atom (per-bond for sticks) transparency settings; see PyMOLConstants repSetSpec. */
    PerGroupTransparentReps() { return ['sticks', 'spheres', 'cartoon', 'surface']; }

    NativeCommandState() { return this.nativeCommands.state(); }

    /** Restricted mode + BMaps' managed-object policy; see PyMOLCommands.run(). */
    RunNativeCommand(text) {
        try {
            return this.nativeCommands.run(text);
        } catch (ex) {
            const error = String((ex && ex.message) || ex);
            return {
                ok: false, output: [error], error, lines: [{ kind: 'error', text: error }],
            };
        }
    }

    CompleteNativeCommand(text) {
        try {
            return this.nativeCommands.complete(text);
        } catch (ex) {
            return [];
        }
    }

    // ------------------------------------------------------------------ styles and colors

    StyleAtom(atom, style) {
        const visAtom = this.atomsByAppAtom.get(atom);
        if (!visAtom) {
            // Hiding an atom that is not loaded is routine; anything else is worth a note.
            if (style !== MolStyles.hidden) console.warn(`VisualizerPyMOL: no canvas atom to style for ${atom}`);
            return;
        }
        let newStyle = style;
        if (!getStyleSpec(newStyle)) {
            console.warn(`Unknown molecule style: ${style}`);
            newStyle = MolStyles.sticks; // as Visualizer3Dmol.makeStyleSpec()
        }
        if (visAtom.style === newStyle) return;
        const residueColored = (spec) => !!(spec.cartoonLike || spec.cartoonColored);
        const wasCartoonLike = residueColored(getStyleSpec(visAtom.style));
        visAtom.style = newStyle;
        this.renderer.markStyleDirty(visAtom);
        // The effective color depends on the style: cartoon-like styles are colored per
        // residue, and a hidden atom's color is only pushed once it shows something.
        this.renderer.markColorDirty(visAtom);
        if (wasCartoonLike || residueColored(getStyleSpec(newStyle))) {
            this.markResidueColorDirty(visAtom);
        }
        if (!getStyleSpec(newStyle).visible && this.picking) this.picking.forgetAtom(visAtom);
    }

    StyleAtoms(style, selectorFn) {
        if (!getStyleSpec(style)) {
            console.warn(`StyleAtoms: unknown molecule style: ${style}`);
            return;
        }
        for (const visAtom of this.atomsById.values()) {
            if (!selectorFn || selectorFn(visAtom.appAtom)) this.StyleAtom(visAtom.appAtom, style);
        }
        this.Redisplay();
    }

    setAtomSelected(atom, selected) {
        const visAtom = this.atomsByAppAtom.get(atom);
        if (!visAtom || visAtom.selected === !!selected) return;
        visAtom.selected = !!selected;
        this.renderer.markColorDirty(visAtom);
        this.markResidueColorDirty(visAtom);
    }

    /** @param {number|string} color a color, or 'default' for the element / chain default */
    setAtomColor(atom, color) {
        // No complaint about unknown atoms: BMaps recolors all atoms, loaded or not.
        const visAtom = this.atomsByAppAtom.get(atom);
        if (!visAtom) return;
        let override = null;
        if (color !== 'default') {
            override = colorToInt(color);
            if (override == null) {
                console.warn(`VisualizerPyMOL: unrecognized color ${JSON.stringify(color)}; using the default.`);
            }
        }
        if (visAtom.colorOverride === override) return;
        visAtom.colorOverride = override;
        this.renderer.markColorDirty(visAtom);
        this.markResidueColorDirty(visAtom);
    }

    atomIsVisible(atom) {
        const visAtom = this.atomsByAppAtom.get(atom);
        return !!visAtom && getStyleSpec(visAtom.style).visible;
    }

    /**
     * Cartoon colors are resolved per residue at flush time, so there is nothing to do once
     * a scene's per-atom colors are in; the hook exists for backends that must batch this.
     */
    refreshCartoonColors() { }

    /** A change on one atom can change the cartoon color of its whole residue. */
    markResidueColorDirty(visAtom) {
        const residue = visAtom.residueKey && this.residues.get(visAtom.residueKey);
        if (!residue) return;
        for (const other of residue) {
            const spec = getStyleSpec(other.style);
            if (spec.cartoonLike || spec.cartoonColored) this.renderer.markColorDirty(other);
        }
    }

    defaultAtomColor(visAtom) {
        const atom = visAtom.appAtom;
        // Maestro's "Element (Light Carbons)": every carbon light grey, whatever it belongs to.
        if (this.colorStyle === ColorStyles.maestro && atom.elem === 'C') return MaestroLikeColors.carbon;
        const mapped = atom.defaultColorMap && atom.defaultColorMap[atom.elem];
        const color = mapped != null ? colorToInt(mapped) : null;
        return color != null ? color : elementColor(atom.elem);
    }

    /**
     * The color PyMOL should show for an atom.
     *   sticks / spheres / lines ...: selected > override > default (element colors, through
     *     atom.defaultColorMap where the app set one), per atom.
     *   cartoon-like styles: ONE color per residue, the highest priority found on any of its
     *     atoms - selected > override > default (the chain color). Per-atom calls for one
     *     residue would otherwise overwrite each other (carbons custom-colored, N / O still
     *     'default'), and PyMOL colors a cartoon segment from a single atom anyway.
     */
    effectiveColor(visAtom) {
        if (getStyleSpec(visAtom.style).cartoonLike && visAtom.residueKey
            && this.residues.get(visAtom.residueKey)) {
            return this.residueColor(visAtom);
        }
        if (visAtom.selected) return SelectionColor;
        if (visAtom.colorOverride != null) return visAtom.colorOverride;
        return this.defaultAtomColor(visAtom);
    }

    /**
     * The color of the residue's cartoon: selected > color override of any of its atoms > the
     * ColorStyle's secondary-structure color > the chain's. Used for cartoon-like styles and for
     * the cartoon_color of a rep set mixing cartoon with atom reps.
     */
    residueColor(visAtom) {
        const residue = visAtom.residueKey ? this.residues.get(visAtom.residueKey) : null;
        if (!residue) {
            if (visAtom.selected) return SelectionColor;
            if (visAtom.colorOverride != null) return visAtom.colorOverride;
            return this.defaultAtomColor(visAtom);
        }
        let override = null;
        for (const other of residue) {
            if (other.selected) return SelectionColor;
            if (override == null && other.colorOverride != null) override = other.colorOverride;
        }
        if (override != null) return override;
        if (this.colorStyle === ColorStyles.maestro) {
            // Maestro's ribbons: red helices, cyan strands, grey loops (BMaps' annotation, the
            // same one the CIF writer hands PyMOL as pymol_ss).
            const ss = VisualizerInterface.getAtomSecondaryStructure(visAtom.appAtom);
            return secondaryStructureColor(ss);
        }
        const chain = visAtom.appAtom.chain;
        return chain && this.proteinChains.includes(chain)
            ? GetChainColor(chain, this.proteinChains)
            : this.defaultAtomColor(visAtom);
    }

    // ------------------------------------------------------------------ surfaces (M2)

    /** Every loaded BMaps atom (the atoms 3Dmol's selectors run over). */
    loadedAppAtoms() {
        return Array.from(this.atomsById.values(), (visAtom) => visAtom.appAtom);
    }

    /**
     * A surface around atoms: see PyMOLSurface.js. Returns its id synchronously.
     *   atoms | atomGroup | displayAtomsFn  the atoms whose patches are shown (loaded ones only)
     *   computingAtomsFn                    the atoms it is computed over (default: the same)
     *   colorFn(atom)                       colour per atom; without it each atom's own colour
     *                                       (selected > custom > element), as 3Dmol's surface
     *                                       takes `atom.color`
     *   opacity                             default 0.8, as Visualizer3Dmol
     */
    DrawSurface({
        atoms, atomGroup, displayAtomsFn, computingAtomsFn, colorFn, opacity=DefaultSurfaceOpacity,
    }={}) {
        let display = [];
        if (displayAtomsFn) {
            display = this.loadedAppAtoms().filter((atom) => displayAtomsFn(atom));
        } else if (atoms) {
            display = [].concat(atoms).filter((atom) => atom && this.atomsByAppAtom.has(atom));
        } else if (atomGroup && typeof atomGroup.hasAtom === 'function') {
            display = this.loadedAppAtoms().filter((atom) => atomGroup.hasAtom(atom));
        } else {
            console.error('DrawSurface: a surface needs to have either atoms, an atom group, or a display selector function defined.');
        }
        const computing = computingAtomsFn
            ? this.loadedAppAtoms().filter((atom) => computingAtomsFn(atom))
            : display;
        const colorOf = (atom) => {
            const color = colorFn ? colorToInt(colorFn(atom)) : null;
            return color != null ? color : this.surfaceDefaultColor(atom);
        };
        return this.surfaces.add({
            computingAtoms: computing,
            displayAtoms: new Set(display),
            colorOf,
            opacity: normalizedOpacity(opacity),
        });
    }

    RemoveSurface(surfaceId) { this.surfaces.remove(surfaceId); }

    /** 3Dmol's surface colour without a colorfunc: the atom's own colour, whatever its style. */
    surfaceDefaultColor(atom) {
        const visAtom = this.atomsByAppAtom.get(atom);
        if (!visAtom) return elementColor(atom && atom.elem);
        if (visAtom.selected) return SelectionColor;
        if (visAtom.colorOverride != null) return visAtom.colorOverride;
        return this.defaultAtomColor(visAtom);
    }

    // ------------------------------------------------------------------ shapes (M2)

    /** Handles as Visualizer3Dmol's: see PyMOLShapes.js. */
    makeShape(kind, spec, shapeOptions) {
        return this.shapes.add(kind, spec, shapeOptions || {});
    }

    RemoveShape(shape) {
        if (!shape) return;
        this.shapes.remove(shape);
        if (this.picking) this.picking.forgetTarget(shape);
    }

    drawLine(start, end, color) { return this.makeShape('line', { start, end, color }); }

    removeLine(line) { this.RemoveShape(line); }

    drawSphere(center, radius, color, shapeOptions) {
        return this.makeShape('sphere', { center, radius, color }, shapeOptions);
    }

    removeSphere(sphere) { this.RemoveShape(sphere); }

    drawCylinder(start, end, radius, color, dashed=false, shapeOptions={}) {
        return this.makeShape('cylinder', {
            start, end, radius, color, dashed,
        }, shapeOptions);
    }

    removeCylinder(cylinder) { this.RemoveShape(cylinder); }

    drawCone(start, end, radius, color, shapeOptions) {
        return this.makeShape('cone', {
            start, end, radius, color,
        }, shapeOptions);
    }

    removeCone(cone) { this.RemoveShape(cone); }

    drawArrow(start, end, radius, color, radiusRatio=1.0, shapeOptions={}) {
        return this.makeShape('arrow', {
            start, end, radius, color, radiusRatio,
        }, shapeOptions);
    }

    removeArrow(arrow) { this.RemoveShape(arrow); }

    // ------------------------------------------------------------------ labels (M2)

    /**
     * A label at the centroid of its atoms (3Dmol addLabel: the loaded atoms `centerFn` /
     * `centerAtomGroup` select), drawn as an HTML overlay - see PyMOLLabels.js.
     */
    drawLabel({
        text, options, centerFn, centerAtomGroup,
    }={}) {
        let atoms = [];
        if (centerFn) {
            atoms = this.loadedAppAtoms().filter((atom) => centerFn(atom));
        } else if (centerAtomGroup && typeof centerAtomGroup.getAtoms === 'function') {
            atoms = centerAtomGroup.getAtoms().filter((atom) => this.atomsByAppAtom.has(atom));
        } else if (centerAtomGroup && typeof centerAtomGroup.hasAtom === 'function') {
            atoms = this.loadedAppAtoms().filter((atom) => centerAtomGroup.hasAtom(atom));
        } else {
            console.warn('drawLabel: label needs either centerFn or centerAtomGroup to specify where it should be drawn');
        }
        const sum = [0, 0, 0];
        let n = 0;
        for (const atom of atoms) {
            const p = atom.getPosition();
            if (!isPosition(p)) continue;
            sum[0] += p[0];
            sum[1] += p[1];
            sum[2] += p[2];
            n++;
        }
        const label = {
            id: this.nextHandleId++, kind: 'label', text, options, centerFn, centerAtomGroup,
        };
        this.labels.add(label, n > 0 ? sum.map((v) => v / n) : null);
        this.updateLabels(true);
        return label;
    }

    removeLabel(label) { if (label) this.labels.remove(label); }

    // ------------------------------------------------------------------ temporary molecules (M2)

    /**
     * A non-interactive (hoverable, not clickable) copy of `atoms`, e.g. a fragment pose of the
     * LigandMod pose cloud. See PyMOLTempMolecules.js. The real atoms' mapping is untouched.
     * @param {object[]} atoms BMaps atoms (their current positions are copied)
     * @param {string} style MolStyles code (default: the atoms' initial style, as 3Dmol)
     * @param {Object<string, *>} [colorMap] element -> colour; others keep element colours
     * @param {number} [opacity] replaces the style's own opacity (3Dmol); default opaque
     * @param {object[]} [atomPair] two atoms: a cyan link cylinder (radius 0.25) between them
     */
    MakeTempMolecule(atoms, style, colorMap={}, opacity, atomPair) {
        const list = [].concat(atoms || []).filter(
            (atom) => atom && typeof atom.getPosition === 'function' && isPosition(atom.getPosition()),
        );
        const tempModel = {
            id: this.nextHandleId++,
            kind: 'tempMolecule',
            atoms: list,
            style,
            colorMap,
            opacity,
        };
        const effectiveStyle = style || (list[0] && list[0].initialStyle) || InitialStyle;
        this.tempMolecules.add(tempModel, list, {
            style: effectiveStyle,
            colors: this.tempColors(list, colorMap),
            opacity: normalizedOpacity(opacity),
        });
        if (atomPair && atomPair[0] && atomPair[1]) {
            // Visualizer3Dmol draws this link SOLID (its drawCylinder's `dashed` defaults to
            // false), whatever the property name says.
            tempModel.dashedCyl = this.drawCylinder(
                atomPair[0].getPosition({ as: 'object' }),
                atomPair[1].getPosition({ as: 'object' }),
                0.25, 'cyan',
            );
        }
        return tempModel;
    }

    /** 3Dmol: `colorMap[elem] || elementColors.defaultColors[elem]` (rasmol). */
    tempColors(atoms, colorMap={}, color=null) {
        const single = color != null && color !== 'default' ? colorToInt(color) : null;
        return Uint32Array.from(atoms, (atom) => {
            if (single != null) return single;
            const mapped = colorMap && colorMap[atom.elem] ? colorToInt(colorMap[atom.elem]) : null;
            return mapped != null ? mapped : elementColor(atom.elem);
        });
    }

    /**
     * Restyle a temporary molecule. Fields left out keep their value (3Dmol would hide the
     * molecule for a missing style and make it opaque for a missing opacity - no caller relies
     * on that). `color` (3Dmol ignores it with a warning) colours the whole molecule;
     * 'default' returns to the colour map.
     */
    UpdateTempMolecule(tempModel, { style, opacity, color }={}) {
        if (!tempModel || !this.tempMolecules.records.has(tempModel)) return;
        if (style !== undefined) tempModel.style = style;
        if (opacity !== undefined) tempModel.opacity = opacity;
        if (color !== undefined) tempModel.color = color;
        this.tempMolecules.update(tempModel, {
            style: style || undefined,
            opacity: opacity === undefined ? undefined : normalizedOpacity(opacity),
            colors: color === undefined ? undefined
                : this.tempColors(tempModel.atoms, tempModel.colorMap, color),
        });
    }

    RemoveTempMolecule(tempModel) {
        if (!tempModel) return;
        if (tempModel.dashedCyl) this.RemoveShape(tempModel.dashedCyl);
        this.tempMolecules.remove(tempModel);
        if (this.picking) {
            this.picking.forgetTargetsWhere((key) => !!key && key.tempMolecule === tempModel);
        }
    }
}
